if (!customElements.get('pj-review-feed')) {
  customElements.define('pj-review-feed', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.list = this.querySelector('[data-review-list]');
      if (!this.list) return;
      this.items = Array.from(this.list.children);
      this.carousel = this.hasAttribute('data-carousel');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.hovered = false;
      this.visible = false;
      this.sort = this.querySelector('[data-review-sort]');
      if (this.sort) this.sort.value = this.dataset.sort || 'newest';
      this.on(this.sort, 'change', () => this.arrange());
      this.on(this.querySelector('[data-review-prev]'), 'click', () => this.move(-1));
      this.on(this.querySelector('[data-review-forward]'), 'click', () => this.move(1));
      this.on(this.querySelector('[data-review-toggle]'), 'click', () => {
        this.paused = !this.paused;
        this.updateToggle();
        this.schedule();
      });
      this.on(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      this.on(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      this.on(this, 'focusin', () => this.schedule());
      this.on(this, 'focusout', () => queueMicrotask(() => this.schedule()));
      this.on(document, 'visibilitychange', () => this.schedule());
      this.on(this.motion, 'change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateToggle();
        this.schedule();
      });
      this.on(this.list, 'scroll', () => { if (!this.animating) this.normalizeLoop(); }, { passive: true });
      this.on(this.list, 'pointerdown', () => { this.dragging = true; this.stopAnimation(); cancelAnimationFrame(this.ticker); });
      this.on(window, 'pointerup', () => { this.dragging = false; this.schedule(); });
      this.on(window, 'pointercancel', () => { this.dragging = false; this.schedule(); });
      this.on(this.list, 'keydown', (event) => {
        if (!this.carousel || event.target !== this.list) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      this.resize = new ResizeObserver(() => {
        if (this.lastWidth !== this.list.clientWidth) {
          this.lastWidth = this.list.clientWidth;
          this.buildLoop();
        }
        this.schedule();
      });
      this.resize.observe(this.list);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
      });
      this.observer.observe(this);
      this.arrange();
      this.updateToggle();
      this.loadRemaining();
    }

    on(target, event, handler, options = {}) {
      target?.addEventListener(event, handler, { ...options, signal: this.controller.signal });
    }

    arrange() {
      const mode = this.sort?.value || this.dataset.sort || 'newest';
      const date = (item) => Number(item.dataset.date) || 0;
      const rating = (item) => Number(item.dataset.rating) || 0;
      const compare = {
        popular: (a, b) => (Number(a.dataset.priority) || 99999) - (Number(b.dataset.priority) || 99999) || date(b) - date(a),
        newest: (a, b) => date(b) - date(a),
        oldest: (a, b) => date(a) - date(b),
        highest: (a, b) => rating(b) - rating(a) || date(b) - date(a),
        lowest: (a, b) => rating(a) - rating(b) || date(b) - date(a),
        name: (a, b) => a.dataset.name.localeCompare(b.dataset.name, document.documentElement.lang || 'en')
      };
      this.items.sort(compare[mode] || compare.newest);
      const limit = Number(this.dataset.limit) || this.items.length;
      const shown = this.carousel ? this.items.slice(0, limit) : this.items;
      this.stopAnimation();
      this.shown = shown;
      this.list.replaceChildren(...shown);
      this.buildLoop();
      this.formatDates();
      this.updatePosition();
      this.schedule();
    }

    async loadRemaining() {
      const link = this.querySelector('[data-review-next]');
      if (!link) return;
      const visited = new Set();
      let next = link.href;
      try {
        while (next && !visited.has(next) && this.isConnected) {
          visited.add(next);
          const url = new URL(next, location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Review request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const feed = html.querySelector('pj-review-feed');
          if (!feed) throw new Error('Review section missing');
          const known = new Set(this.items.map(item => item.dataset.reviewId));
          const incoming = Array.from(feed.querySelectorAll('[data-review-id]')).filter(item => !known.has(item.dataset.reviewId));
          if (!incoming.length) break;
          this.items.push(...incoming);
          next = feed.querySelector('[data-review-next]')?.getAttribute('href');
          if (next) link.href = next;
        }
        if (!this.isConnected) return;
        link.remove();
        this.arrange();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.arrange();
      }
    }

    formatDates() {
      const locale = document.documentElement.lang || 'en';
      const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const absolute = new Intl.DateTimeFormat(locale, { dateStyle: 'long' });
      this.list.querySelectorAll('[data-pj-review-time]').forEach(element => {
        const date = new Date(element.dateTime);
        if (Number.isNaN(date.getTime())) return;
        const seconds = (date.getTime() - Date.now()) / 1000;
        const [unit, divisor] = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60], ['second', 1]].find(([, value]) => Math.abs(seconds) >= value) || ['second', 1];
        element.textContent = relative.format(Math.round(seconds / divisor), unit);
        element.title = absolute.format(date);
      });
    }

    updatePosition() {
      if (!this.carousel) return;
      const controls = this.querySelector('[data-review-controls]');
      if (controls) controls.hidden = this.shown.length < 2;
    }

    buildLoop() {
      if (!this.carousel || !this.shown) return;
      this.stopAnimation();
      this.list.replaceChildren(...this.shown);
      this.bandWidth = 0;
      if (this.shown.length < 2 || !this.list.clientWidth) return;
      const clone = card => {
        const copy = card.cloneNode(true);
        copy.dataset.reviewClone = 'true';
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a, button, summary, [tabindex]').forEach(element => element.setAttribute('tabindex', '-1'));
        return copy;
      };
      const step = this.shown[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.list).columnGap || 0);
      const repeats = Math.max(1, Math.ceil((this.list.clientWidth + step) / (step * this.shown.length)));
      const band = [...this.shown];
      for (let i = 1; i < repeats; i++) band.push(...this.shown.map(clone));
      this.list.replaceChildren(...band.map(clone), ...band, ...band.map(clone));
      this.bandWidth = step * band.length;
      this.list.scrollLeft = this.bandWidth;
      this.updatePosition();
    }

    normalizeLoop() {
      if (!this.bandWidth) return;
      const left = this.list.scrollLeft;
      if (left < this.bandWidth) this.list.scrollLeft = left + this.bandWidth;
      else if (left >= this.bandWidth * 2) this.list.scrollLeft = left - this.bandWidth;
    }

    move(direction) {
      if (!this.carousel || !this.list.firstElementChild) return;
      this.stopAnimation();
      cancelAnimationFrame(this.ticker);
      const max = this.list.scrollWidth - this.list.clientWidth;
      if (max <= 2) return;
      const start = this.list.scrollLeft;
      const step = this.list.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(this.list).columnGap || 0);
      const target = start + direction * step;
      const duration = this.motion.matches ? 0 : Number(this.dataset.duration) || 500;
      const began = performance.now();
      this.animating = true;
      const animate = (now) => {
        const progress = duration ? Math.min(1, (now - began) / duration) : 1;
        this.list.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
        if (progress < 1) this.frame = requestAnimationFrame(animate);
        else { this.stopAnimation(); this.normalizeLoop(); this.schedule(); }
      };
      this.frame = requestAnimationFrame(animate);
    }

    stopAnimation() {
      cancelAnimationFrame(this.frame);
      this.animating = false;
    }

    updateToggle() {
      const toggle = this.querySelector('[data-review-toggle]');
      if (toggle) {
        toggle.textContent = this.paused ? toggle.dataset.play : toggle.dataset.pause;
        toggle.setAttribute('aria-pressed', String(this.paused));
      }
    }

    schedule() {
      cancelAnimationFrame(this.ticker);
      if (!this.isConnected || !this.carousel || !this.bandWidth || this.animating || this.dragging || this.dataset.autoplay !== 'true' || this.paused || !this.visible || document.hidden || this.contains(document.activeElement) || (this.hovered && this.dataset.pauseHover === 'true')) return;
      let previous = performance.now();
      let position = this.list.scrollLeft;
      const tick = now => {
        const step = this.shown[0].getBoundingClientRect().width + parseFloat(getComputedStyle(this.list).columnGap || 0);
        position += step * Math.min(now - previous, 64) / (Number(this.dataset.interval) || 5000);
        if (position >= this.bandWidth * 2) position -= this.bandWidth;
        this.list.scrollLeft = position;
        previous = now;
        this.normalizeLoop();
        this.ticker = requestAnimationFrame(tick);
      };
      this.ticker = requestAnimationFrame(tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.ticker);
      this.stopAnimation();
      this.resize?.disconnect();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
